<template>
  <div>
    <div class="d-flex flex-wrap align-center justify-space-between" style="gap: 12px;">
      <h2 class="mb-0">Relatório Geral de Ordem de Pagamento</h2>
      <v-btn outlined color="primary" @click="$router.push('/relatorios#financeiro')">
        <v-icon left>mdi-arrow-left</v-icon>
        Voltar ao menu
      </v-btn>
    </div>

    <v-alert v-if="modoLimitado" type="warning" dense text class="mt-3">
      A consulta geral não retornou dados; exibindo apenas as Ordens de Pagamento em que você é owner.
    </v-alert>

    <v-row class="mt-3" dense>
      <v-col cols="6" md="3"><v-card outlined class="pa-3 kpi"><div class="kpi-t">Ordens de Pagamento</div><div class="kpi-v">{{ qtdOps }}</div></v-card></v-col>
      <v-col cols="6" md="3"><v-card outlined class="pa-3 kpi"><div class="kpi-t">Valor das OPs</div><div class="kpi-v">{{ formatarMoedaPtBr(totalOps) }}</div></v-card></v-col>
      <v-col cols="6" md="3"><v-card outlined class="pa-3 kpi"><div class="kpi-t">Valor das despesas (linhas)</div><div class="kpi-v">{{ formatarMoedaPtBr(totalDespesas) }}</div></v-card></v-col>
      <v-col cols="6" md="3"><v-card outlined class="pa-3 kpi kpi-red"><div class="kpi-t">OPs vencidas</div><div class="kpi-v">{{ qtdVencidas }}</div></v-card></v-col>
    </v-row>

    <div class="d-flex flex-wrap align-center mt-4 mb-2" style="gap: 12px;">
      <v-text-field v-model="filtros.op" label="N° OP" solo hide-details clearable class="c-filtro" />
      <v-select v-model="filtros.tipo" :items="opcoes('tipo')" label="Tipo" solo hide-details clearable class="c-filtro" />
      <v-select v-model="filtros.status" :items="opcoes('status')" label="Status (todos)" solo hide-details clearable multiple class="c-filtro" />
      <v-select v-model="filtros.tipoCredor" :items="opcoes('tipoCredor')" label="Tipo do Credor" solo hide-details clearable class="c-filtro" />
      <v-text-field v-model="filtros.credor" label="Nome do Credor" solo hide-details clearable class="c-filtro" />
      <v-text-field v-model="filtros.documento" label="CPF / CNPJ" solo hide-details clearable class="c-filtro" />
      <v-text-field v-model="filtros.job" label="Job" solo hide-details clearable class="c-filtro" />
      <v-text-field v-model="filtros.os" label="Ordem de Serviço" solo hide-details clearable class="c-filtro" />
    </div>
    <div class="d-flex flex-wrap align-center mb-4" style="gap: 12px;">
      <v-text-field v-model="filtros.centroCusto" label="Centro de Custo" solo hide-details clearable class="c-filtro" />
      <v-text-field v-model="filtros.emitente" label="Emitente" solo hide-details clearable class="c-filtro" />
      <v-text-field v-model="filtros.requisitante" label="Requisitante" solo hide-details clearable class="c-filtro" />
      <v-text-field v-model="filtros.aprovador" label="Aprovador" solo hide-details clearable class="c-filtro" />
      <v-text-field v-model="filtros.emissaoDe" type="date" label="Emissão de" outlined dense background-color="white" hide-details clearable class="c-filtro-data" />
      <v-text-field v-model="filtros.emissaoAte" type="date" label="Emissão até" outlined dense background-color="white" hide-details clearable class="c-filtro-data" />
      <v-text-field v-model="filtros.vencimentoDe" type="date" label="Vencimento de" outlined dense background-color="white" hide-details clearable class="c-filtro-data" />
      <v-text-field v-model="filtros.vencimentoAte" type="date" label="Vencimento até" outlined dense background-color="white" hide-details clearable class="c-filtro-data" />
      <v-checkbox v-model="filtros.somenteVencidas" label="Somente vencidas" color="red" hide-details class="mt-0" />

      <v-btn color="primary" :loading="loading" @click="buscarDados">
        <v-icon left>mdi-refresh</v-icon>
        Recarregar
      </v-btn>
      <v-menu>
        <template v-slot:activator="{ on }">
          <v-btn color="primary" outlined v-on="on" :disabled="loading || linhas.length === 0">
            <v-icon left>mdi-file-export-outline</v-icon>
            Exportar
          </v-btn>
        </template>
        <v-list>
          <v-list-item link @click="gerarExcel"><v-list-item-title>Excel</v-list-item-title></v-list-item>
          <v-list-item link @click="gerarPdf"><v-list-item-title>PDF</v-list-item-title></v-list-item>
        </v-list>
      </v-menu>
    </div>

    <v-data-table
      :headers="headers"
      :items="linhas"
      :loading="loading"
      item-key="_id"
      show-expand
      single-expand
      :footer-props="{ 'items-per-page-text': 'Linhas por página:', 'page-text': '{0}-{1} de {2}', 'items-per-page-options': [25, 50, 100, -1] }"
      :items-per-page="50"
      class="elevation-1 tabela-geral"
    >
      <template v-slot:item="{ item, expand, isExpanded, headers: hs }">
        <tr :class="item.vencida ? 'linha-vencida' : ''">
          <td v-for="h in hs" :key="h.value" :class="h.align === 'end' ? 'text-end' : ''">
            <template v-if="h.value === 'data-table-expand'">
              <v-btn icon small @click="expand(!isExpanded)"><v-icon>{{ isExpanded ? 'mdi-chevron-up' : 'mdi-chevron-down' }}</v-icon></v-btn>
            </template>
            <template v-else-if="['dataEmissao','dataVencimento'].includes(h.value)">{{ formatarDia(item[h.value]) }}</template>
            <template v-else-if="['valorOp','valorDespesa'].includes(h.value)">{{ formatarMoedaPtBr(item[h.value]) }}</template>
            <template v-else-if="h.value === 'diasAtraso'"><span v-if="item.diasAtraso" class="red--text font-weight-bold">{{ item.diasAtraso }}</span></template>
            <template v-else>{{ item[h.value] }}</template>
          </td>
        </tr>
      </template>

      <template v-slot:expanded-item="{ item, headers: hs }">
        <td :colspan="hs.length" class="py-2 grey lighten-4">
          <div v-for="a in aprovadoresDaOp(item._opId)" :key="a._k" class="apr-linha">
            <b>{{ a.tipoAprovador }}</b> — {{ a.aprovador }} · divisão: {{ a.divisao || '-' }} · {{ a.decisao }}
            <span v-if="a.dataDecisao">em {{ formatarDia(a.dataDecisao) }}</span>
            <span v-if="a.registro === 'Cancelado'" class="grey--text"> (cancelado)</span>
          </div>
          <div v-if="!aprovadoresDaOp(item._opId).length" class="apr-linha">Sem aprovadores registrados.</div>
        </td>
      </template>
    </v-data-table>
  </div>
</template>

<script>
import * as XLSX from "xlsx";
import relatorioService from "@/views/Relatorios/services/relatorioOrdemDePagamentoGeralService";
import { formatarDia } from "@/views/Relatorios/services/relatorioGeralUtils";
import { exportarPdfTabela, formatarMoedaPtBr, normalizarTexto } from "@/views/Relatorios/base/relatorioBaseUtils";
import relatorioPermissionMixin from "@/mixins/relatorioPermissionMixin";

export default {
  name: "RelatorioOrdemDePagamentoGeralView",
  mixins: [relatorioPermissionMixin],
  data() {
    return {
      relatorioSchemas: ["OrdemDePagamento"],
      relatorioFiltrarPorOwner: false,
      loading: false,
      modoLimitado: false,
      dados: [],
      aprovadores: [],
      filtros: {
        op: "", tipo: null, status: [], tipoCredor: null, credor: "", documento: "", job: "", os: "",
        centroCusto: "", emitente: "", requisitante: "", aprovador: "",
        emissaoDe: null, emissaoAte: null, vencimentoDe: null, vencimentoAte: null, somenteVencidas: false
      },
      headers: [
        { text: "Empresa", value: "empresa" },
        { text: "Tipo", value: "tipo" },
        { text: "N° OP", value: "op" },
        { text: "Data de Emissão", value: "dataEmissao" },
        { text: "Data de Vencimento", value: "dataVencimento" },
        { text: "Status", value: "status" },
        { text: "Valor da OP", value: "valorOp", align: "end" },
        { text: "N° da Despesa", value: "numeroDespesa" },
        { text: "Grupo da Despesa", value: "grupoDespesa" },
        { text: "Valor da Despesa", value: "valorDespesa", align: "end" },
        { text: "Tipo do Credor", value: "tipoCredor" },
        { text: "Nome do Credor", value: "nomeCredor" },
        { text: "CPF / CNPJ", value: "documento" },
        { text: "N° Centro de Custo", value: "numeroCentroCusto" },
        { text: "Centro de Custo", value: "centroCusto" },
        { text: "Job", value: "job" },
        { text: "Ordem de Serviço", value: "os" },
        { text: "Emitente", value: "emitente" },
        { text: "Requisitante", value: "requisitante" },
        { text: "Observação (despesa)", value: "observacaoDespesa" },
        { text: "Observação (justificativa da OP)", value: "justificativa" },
        { text: "Situação do Pagamento", value: "situacaoPagamento" },
        { text: "Dias em atraso", value: "diasAtraso", align: "end" },
        { text: "", value: "data-table-expand", sortable: false }
      ]
    };
  },
  computed: {
    linhas() {
      const f = this.filtros;
      const t = (v) => normalizarTexto(v).toLowerCase();
      const inc = (campo, filtro) => !filtro || t(campo).includes(t(filtro));
      const noPeriodo = (dia, de, ate) => {
        if (!de && !ate) return true;
        if (!dia) return false;
        return (!de || dia >= de) && (!ate || dia <= ate);
      };
      return this.dados.filter((d) => {
        if (!inc(d.op, f.op)) return false;
        if (f.tipo && d.tipo !== f.tipo) return false;
        if (f.status && f.status.length && !f.status.includes(d.status)) return false;
        if (f.tipoCredor && d.tipoCredor !== f.tipoCredor) return false;
        if (!inc(d.nomeCredor, f.credor) || !inc(d.documento, f.documento)) return false;
        if (!inc(d.job, f.job) || !inc(d.os, f.os)) return false;
        if (!inc(`${d.numeroCentroCusto} ${d.centroCusto}`, f.centroCusto)) return false;
        if (!inc(d.emitente, f.emitente) || !inc(d.requisitante, f.requisitante)) return false;
        if (f.aprovador) {
          const nomes = `${d.aprRequisitante} ${d.aprCoordenador} ${d.aprGestor} ${d.aprFinanceiro}`;
          if (!inc(nomes, f.aprovador)) return false;
        }
        if (!noPeriodo(d.dataEmissao, f.emissaoDe, f.emissaoAte)) return false;
        if (!noPeriodo(d.dataVencimento, f.vencimentoDe, f.vencimentoAte)) return false;
        if (f.somenteVencidas && !d.vencida) return false;
        return true;
      });
    },
    opsUnicas() {
      const mapa = {};
      this.linhas.forEach((l) => { mapa[l._opId] = l; });
      return Object.values(mapa);
    },
    qtdOps() { return this.opsUnicas.length; },
    // Valor da OP repete em cada despesa: soma apenas 1x por OP (evita duplicar o total).
    totalOps() { return this.opsUnicas.reduce((a, l) => a + l.valorOp, 0); },
    totalDespesas() { return this.linhas.reduce((a, l) => a + l.valorDespesa, 0); },
    qtdVencidas() { return this.opsUnicas.filter((l) => l.vencida).length; },
    descricaoFiltros() {
      const f = this.filtros;
      const p = [];
      if (f.op) p.push(`OP: ${f.op}`);
      if (f.tipo) p.push(`Tipo: ${f.tipo}`);
      if (f.status && f.status.length) p.push(`Status: ${f.status.join(", ")}`);
      if (f.tipoCredor) p.push(`Tipo do credor: ${f.tipoCredor}`);
      if (f.credor) p.push(`Credor: ${f.credor}`);
      if (f.documento) p.push(`CPF/CNPJ: ${f.documento}`);
      if (f.job) p.push(`Job: ${f.job}`);
      if (f.os) p.push(`OS: ${f.os}`);
      if (f.centroCusto) p.push(`Centro de custo: ${f.centroCusto}`);
      if (f.emitente) p.push(`Emitente: ${f.emitente}`);
      if (f.requisitante) p.push(`Requisitante: ${f.requisitante}`);
      if (f.aprovador) p.push(`Aprovador: ${f.aprovador}`);
      if (f.emissaoDe || f.emissaoAte) p.push(`Emissão: ${formatarDia(f.emissaoDe) || "..."} a ${formatarDia(f.emissaoAte) || "..."}`);
      if (f.vencimentoDe || f.vencimentoAte) p.push(`Vencimento: ${formatarDia(f.vencimentoDe) || "..."} a ${formatarDia(f.vencimentoAte) || "..."}`);
      if (f.somenteVencidas) p.push("Somente vencidas");
      return p.length ? p.join(" | ") : "Sem filtros";
    }
  },
  methods: {
    formatarMoedaPtBr,
    formatarDia,
    opcoes(campo) {
      return Array.from(new Set(this.dados.map((d) => d[campo]).filter(Boolean))).sort();
    },
    aprovadoresDaOp(opId) {
      return this.aprovadores.filter((a) => a._opId === opId);
    },
    async buscarDados() {
      this.loading = true;
      try {
        const r = await relatorioService.listar();
        this.dados = r.linhas || [];
        this.modoLimitado = !!r.modoLimitado;
        // associa aprovadores ao id interno da OP (pelo número da OP)
        const idPorNumero = {};
        this.dados.forEach((d) => { idPorNumero[d.op] = d._opId; });
        this.aprovadores = (r.aprovadores || []).map((a, i) => ({ ...a, _k: i, _opId: idPorNumero[a.op] }));
      } catch (e) {
        this.dados = [];
        this.aprovadores = [];
        this.$notfy?.error?.("Não foi possível carregar o relatório de ordens de pagamento.");
      } finally {
        this.loading = false;
      }
    },
    gerarExcel() {
      if (!this.linhas.length) return;
      const principal = this.linhas.map((d) => ({
        Empresa: d.empresa,
        Tipo: d.tipo,
        "N° OP": d.op,
        "Data de Emissão": formatarDia(d.dataEmissao),
        "Data de Vencimento": formatarDia(d.dataVencimento),
        Status: d.status,
        "Valor da OP": d.valorOp,
        "Item": d.itemDespesa,
        "N° da Despesa": d.numeroDespesa,
        "Descrição da Despesa": d.descricaoDespesa,
        "Grupo da Despesa": d.grupoDespesa,
        "Valor da Despesa": d.valorDespesa,
        "Tipo do Credor": d.tipoCredor,
        "Nome do Credor": d.nomeCredor,
        "CPF / CNPJ": d.documento,
        "N° Centro de Custo": d.numeroCentroCusto,
        "Centro de Custo": d.centroCusto,
        Job: d.job,
        "Ordem de Serviço": d.os,
        Emitente: d.emitente,
        Requisitante: d.requisitante,
        "Observação (despesa)": d.observacaoDespesa,
        "Observação (justificativa da OP)": d.justificativa,
        "Situação do Pagamento": d.situacaoPagamento,
        "Dias em atraso": d.diasAtraso || "",
        "Requisitante (aprovação)": d.aprRequisitante, "Decisão Requisitante": d.decRequisitante, "Data Requisitante": d.datRequisitante,
        Coordenador: d.aprCoordenador, "Decisão Coordenador": d.decCoordenador, "Data Coordenador": d.datCoordenador,
        Gestor: d.aprGestor, "Decisão Gestor": d.decGestor, "Data Gestor": d.datGestor,
        Financeiro: d.aprFinanceiro, "Decisão Financeiro": d.decFinanceiro, "Data Financeiro": d.datFinanceiro
      }));

      const opsFiltradas = new Set(this.linhas.map((l) => l._opId));
      const aprov = this.aprovadores
        .filter((a) => opsFiltradas.has(a._opId))
        .map((a) => ({
          "N° OP": a.op, "Status da OP": a.statusOp, "Tipo do Aprovador": a.tipoAprovador, Aprovador: a.aprovador,
          Divisão: a.divisao, Decisão: a.decisao, "Data de Decisão": formatarDia(a.dataDecisao), Registro: a.registro
        }));

      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(principal), "Ordens de Pagamento");
      if (aprov.length) XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(aprov), "Aprovadores");
      XLSX.writeFile(wb, "Relatório Geral de Ordem de Pagamento.xlsx");
    },
    async gerarPdf() {
      if (!this.linhas.length) return;
      await exportarPdfTabela({
        headers: this.headers.filter((h) => h.value !== "data-table-expand"),
        rows: this.linhas,
        nomeArquivo: "Relatório Geral de Ordem de Pagamento.pdf",
        tituloRelatorio: "Relatório Geral de Ordem de Pagamento",
        subtituloRelatorioLinhas: [this.descricaoFiltros],
        pageFormat: "a3",
        estilos: { fontSize: 6.5 },
        formatadoresPorCampo: {
          dataEmissao: formatarDia,
          dataVencimento: formatarDia,
          valorOp: (v) => formatarMoedaPtBr(v),
          valorDespesa: (v) => formatarMoedaPtBr(v),
          diasAtraso: (v) => (v ? String(v) : "")
        },
        resumoFinalLinhas: [
          `Ordens de Pagamento: ${this.qtdOps} | Vencidas: ${this.qtdVencidas}`,
          `Valor das OPs: ${formatarMoedaPtBr(this.totalOps)} | Valor das despesas: ${formatarMoedaPtBr(this.totalDespesas)}`
        ]
      });
    }
  }
  // A busca inicial é disparada pelo relatorioPermissionMixin assim que o acesso é confirmado.
};
</script>

<style scoped>
.c-filtro { max-width: 200px !important; }
.c-filtro-data { max-width: 200px !important; }
::v-deep .v-data-table-header th { white-space: nowrap; vertical-align: middle; }
.kpi { border-left: 4px solid #1976d2 !important; }
.kpi-red { border-left-color: #d32f2f !important; }
.kpi-t { font-size: 12px; color: #666; }
.kpi-v { font-size: 20px; font-weight: 600; }
.linha-vencida { background: #ffebee; }
.apr-linha { font-size: 13px; padding: 2px 0; }
.tabela-geral { overflow-x: auto; }
</style>
